import React, { useState, useEffect, useCallback } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { showNotification } from "aldoo-ra/Notification"
import {
  InviteUserToLicense,
  GetLicenseInvites,
  RevokeLicenseInvite,
} from "aldoo-ra/Admin/ProductLicensing/api"
import { EnvelopeIcon, UserPlusIcon, XMarkIcon, KeyIcon } from "@heroicons/react/24/outline"

/**
 * Invite teammates to a multi-seat license. Shows the number of available
 * keys (free seats); each invite emails the license key + activation
 * instructions and reserves a seat until accepted or revoked. When no
 * seats are free, points the owner to the Details tab to purchase more.
 */
export default function InviteUsers({ license, onGoToDetails }) {
  const texts = TextBundle({ id: "activate_license" })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [sending, setSending] = useState(false)
  const [inviteData, setInviteData] = useState(null)

  const isSeatLicense = license.users_per_license > 0 || !!license.subscriptionId

  const loadInvites = useCallback(async () => {
    const res = await GetLicenseInvites(license._id)
    if (res?.result) setInviteData(res)
  }, [license._id])

  useEffect(() => {
    if (license.isOwner && isSeatLicense) loadInvites()
  }, [license.isOwner, isSeatLicense, loadInvites])

  if (!license.isOwner || !isSeatLicense) return null

  const availableSeats = inviteData?.availableSeats ?? 0
  const pendingInvites = (inviteData?.invites || []).filter((i) => i.status === "pending")

  const sendInvite = async () => {
    if (!email.trim()) return
    setSending(true)
    try {
      const res = await InviteUserToLicense({ licenseId: license._id, email: email.trim() })
      if (res?.error) {
        showNotification({
          message: res.message || res.error,
          className: "bg-error text-white",
        })
      } else {
        showNotification({
          message: res.emailSent
            ? texts?.invite_sent_notification
            : texts?.invite_created_no_email,
          className: res.emailSent ? "bg-primary text-white" : "bg-error text-white",
        })
        setEmail("")
        await loadInvites()
      }
    } finally {
      setSending(false)
    }
  }

  const revoke = async (invite) => {
    const res = await RevokeLicenseInvite(invite._id)
    if (res?.result) {
      showNotification({
        message: texts?.invite_revoked_notification,
        className: "bg-primary text-white",
      })
      await loadInvites()
    }
  }

  return (
    <div className="mb-4">
      <div className="w-fit flex flex-col sm:flex-row items-center justify-end gap-1 sm:gap-5 sm:paper_30 rounded-full p-2 sm:pl-6 mx-auto sm:mr-0">
        <Typography variant="txt" className="block text-text">
          {availableSeats > 0
            ? `${availableSeats} ${availableSeats === 1 ? texts?.available_key_singular : texts?.available_keys_plural}`
            : texts?.no_available_keys}
          {pendingInvites.length > 0 && ` · ${pendingInvites.length} ${texts?.pending_invites_label}`}
        </Typography>

        <Button variant="primary" onClick={() => setDialogOpen(true)}>
          <UserPlusIcon className="w-4 h-4 mr-2" />
          {texts?.invite_users_btn}
        </Button>
      </div>

      {/* Pending invites (removable) */}
      {pendingInvites.length > 0 && (
        <div className="mt-3 space-y-2">
          {pendingInvites.map((invite) => (
            <div
              key={invite._id}
              className="flex items-center gap-3 bg-canvas border border-bordered rounded-xl px-4 py-2"
            >
              <EnvelopeIcon className="w-4 h-4 text-text_secondary shrink-0" />
              <span className="text-sm text-text flex-1 truncate">{invite.email}</span>
              <span className="text-xs text-warning shrink-0">
                {texts?.invite_pending_badge}
              </span>
              <button
                onClick={() => revoke(invite)}
                title={texts?.revoke_invite}
                className="p-1 text-text_secondary hover:text-error transition-colors shrink-0"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Invite dialog */}
      <Modal
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        variant="snap_compact"
        zIndex={500}
        closeOnEscape={true}
        closeOnClickOutside={true}
      >
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <KeyIcon className="w-5 h-5 text-primary" />
            <Typography variant="h4">
              {texts?.invite_dialog_title}
            </Typography>
          </div>

          <Typography 
            variant="txt1_dim" 
            className="block leading-relaxed mb-4"
          >
            {texts?.invite_dialog_instructions}
          </Typography>

          <Typography variant="txt1" className="block text-text mb-6">
            {availableSeats > 0 ? (
              <>
                {texts?.available_keys_label}{" "}
                <span className="font-semibold text-text">
                  {availableSeats}
                </span>
              </>
            ) : (
              <span className="text-warning">
                {texts?.no_keys_warning}
              </span>
            )}
          </Typography>

          {availableSeats > 0 ? (
            <div className="flex flex-col gap-2">
              <Input
                type="email"
                variant="snap_compact"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={texts?.invite_email_placeholder}
                className="flex-1"
                onKeyDown={(e) => e.key === "Enter" && !sending && sendInvite()}
              />

              <Button 
                variant="primary" 
                disabled={sending || !email.trim()} 
                onClick={sendInvite}
                className="flex-1"
              >
                {sending ? texts?.sending : texts?.send_invite_btn}
              </Button>
            </div>
          ) : (
            <Button
              variant="primary"
              className="w-full"
              onClick={() => {
                setDialogOpen(false)
                onGoToDetails?.()
              }}
            >
              {texts?.purchase_additional_seats_btn}
            </Button>
          )}
        </div>
      </Modal>
    </div>
  )
}
